import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
} from "@mui/material";

import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import { getReports } from "app/services/api/reports";
import ClosureReport from "./ClosuerReport";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import SalesStatisticsReport from "./SalesStatisticsReport";
import ClientsReport from "./ClientsReport.js";
import ExternalOrdersReport from "./ExternalOrdersReport";
import ProductSalesReport from "./ProductSalesReport";
// Remove the search functionality

const DSOKReportsOverview = () => {
  const { t } = useTranslation();
  // Must match the payment.method values the backend actually stores on realized orders
  // (card, cash, invoice, web, alreadyPaid) + "none" for orders with no payment method.
  const ALL_PAYMENT_METHODS = [
    "card",
    "cash",
    "invoice",
    "web",
    "alreadyPaid",
    "none",
  ];

  const [filter, setFilter] = useState({
    reportType: null,
    dateFrom: null,
    dateTo: null,
    month: null,
    year: null,
    range: null,
    paymentMethods: [],
  });
  useEffect(() => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      reportType: null,
      dateFrom: null,
      dateTo: null,
      month: null,
      year: null,
      range: null,
    }));
  }, []);

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [closureData, setClosureData] = useState(false);
  const [salesStatisticsData, setSalesStatisticsData] = useState(false);
  const [clientsData, setClientsData] = useState(false);
  const [externalOrdersData, setExternalOrdersData] = useState(false);
  const [productSalesData, setProductSalesData] = useState(false);
  const [noDataPlaceholder, setNoDataPlaceholder] = useState(
    <JumboListNoDataPlaceholder />
  );
  // Safely read dsokSettings from localStorage (supports nested shape too)
  let dsokSettings = null;
  try {
    const rawSettings = localStorage.getItem("user");
    const parsed = rawSettings ? JSON.parse(rawSettings) : {};
    // Support both: { customer: {...} } and { dsokSettings: { customer: {...} } }
    dsokSettings = parsed?.customer?.dsokSettings
      ? parsed?.customer?.dsokSettings
      : null;
  } catch (e) {
    dsokSettings = null;
  }
  let selectedLocation = {};
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const firstLocationCode = locations?.["1"]?.code;
    setFilter((prevFilter) => ({
      ...prevFilter,
      year: currentYear,
      location: firstLocationCode,
      month: "today",
    }));
  }, []);
  const updateFilter = (newFilter) => {
    setFilter({ ...filter, ...newFilter });
  };
  const handleButtonClick = (newFilter) => {
    setNoDataPlaceholder(<JumboListNoDataPlaceholder />);
    setClosureData(false);
    setSalesStatisticsData(false);
    setClientsData(false);
    setExternalOrdersData(false);
    setProductSalesData(false);
    setFilter({ ...filter, ...newFilter });
  };

  const loadData = async () => {
    setLoading(true);
    setNoDataPlaceholder();
    setClosureData(false);
    setSalesStatisticsData(false);
    setClientsData(false);
    setExternalOrdersData(false);
    setProductSalesData(false);
    const data = await getReports(
      filter.reportType,
      filter.dateFrom,
      filter.dateTo,
      filter.month,
      filter.year,
      filter.location,
      filter.paymentMethods
    );
    if (data?.total === 0 || data?.total === null) {
      setNoDataPlaceholder(<JumboListNoDataPlaceholder />);
      setLoading(false);
      return;
    }

    switch (filter.reportType) {
      case "closure":
        setSalesStatisticsData(false);
        setClosureData(data);
        break;
      case "salesStatistics":
        // const { vatList, total, vat, base } = data;
        setClosureData(false);
        setSalesStatisticsData(data);
        break;
      case "clients":
        setClosureData(false);
        setSalesStatisticsData(false);
        setClientsData(data);
        break;
      case "externalOrders":
        setClosureData(false);
        setSalesStatisticsData(false);
        setClientsData(false);
        setExternalOrdersData(data);
        break;
      case "productSales":
        setClosureData(false);
        setSalesStatisticsData(false);
        setClientsData(false);
        setExternalOrdersData(false);
        setProductSalesData(data);
        break;
      default:
        break;
    }

    setLoading(false);
  };

  useEffect(() => {}, [filter]);
  let canDisplaySubmitButtons = false;
  // Externé objednávky (dsokProcessedOrders) nie sú viazané na lokáciu – lokáciu nevyžadujeme.
  const locationRequired = filter.reportType !== "externalOrders";
  if (
    filter.month &&
    filter.month !== "custom" &&
    filter.reportType !== null &&
    (!locationRequired || filter.location)
  ) {
    canDisplaySubmitButtons = true;
  } else if (
    filter.month === "custom" &&
    filter.dateFrom &&
    filter.dateTo &&
    filter.reportType !== null &&
    (!locationRequired || filter.location)
  ) {
    canDisplaySubmitButtons = true;
  }
  const locations = dsokSettings?.customer?.locations || {};
  const locationNames =
    locations && typeof locations === "object" ? Object.keys(locations) : [];

  const locationsMenuItems = locationNames.map((key) => {
    const locationData = locations[key];
    const isFirstLocation = key === "1";
    const selected = isFirstLocation ? true : false;
    const locationCode = locationData?.code;
    if (selected) {
      selectedLocation = locationData;
    }
    return (
      <MenuItem value={locationCode} key={locationCode} selected={selected}>
        {locationData?.name +
          " - " +
          locationData?.city +
          " - " +
          locationData?.street}
      </MenuItem>
    );
  });
  return (
    <>
      <Grid item md={12} xs={12}>
        <Box sx={{ display: "flex", justifyContent: "left", mt: 2, mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => handleButtonClick({ reportType: "closure" })}
            sx={{ mx: 1 }}
            color={filter.reportType === "closure" ? "secondary" : "primary"}
          >
            {t("pages.dsokReports.closure")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleButtonClick({ reportType: "salesStatistics" })}
            sx={{ mx: 1 }}
            color={
              filter.reportType === "salesStatistics" ? "secondary" : "primary"
            }
          >
            {t("pages.dsokReports.salesStatistics")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleButtonClick({ reportType: "clients" })}
            sx={{ mx: 1 }}
            color={filter.reportType === "clients" ? "secondary" : "primary"}
          >
            {t("pages.dsokReports.clientsOrders")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleButtonClick({ reportType: "externalOrders" })}
            sx={{ mx: 1 }}
            color={
              filter.reportType === "externalOrders" ? "secondary" : "primary"
            }
          >
            {t("pages.dsokReports.externalOrders", {
              defaultValue: "Externé objednávky",
            })}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleButtonClick({ reportType: "productSales" })}
            sx={{ mx: 1 }}
            color={
              filter.reportType === "productSales" ? "secondary" : "primary"
            }
          >
            {t("pages.dsokReports.productSales", {
              defaultValue: "Predaje po produktoch",
            })}
          </Button>
        </Box>
      </Grid>
      <Grid item md={12} xs={12}>
        <Card>
          <Grid container columnSpacing={2} rowGap={2} mb={2}>
            <Grid item md={2} xs={12}>
              <Div sx={{ mt: 2, mb: 0, mx: 2 }}>
                <TextField
                  select
                  label={t("pages.dsokReports.selectRange")}
                  value={filter.month || ""}
                  onChange={(e) => updateFilter({ month: e.target.value })}
                  sx={{ width: "100%" }}
                  // InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="custom">
                    {t("pages.dsokReports.custom")}
                  </MenuItem>
                  <MenuItem value="today">
                    {t("pages.dsokReports.today")}
                  </MenuItem>
                  <MenuItem value="yesterday">
                    {t("pages.dsokReports.yesterday")}
                  </MenuItem>
                  <MenuItem value="thisYear">
                    {t("pages.dsokReports.thisYear")}
                  </MenuItem>
                  <MenuItem value="lastYear">
                    {t("pages.dsokReports.lastYear")}
                  </MenuItem>
                  {Array.from({ length: new Date().getMonth() + 1 }, (_, i) => {
                    const monthName = t(
                      `months.${new Date(0, i)
                        .toLocaleString("en", { month: "long" })
                        .toLowerCase()}`
                    );
                    const monthNumber = i + 1;
                    return (
                      <MenuItem key={monthNumber} value={monthNumber}>
                        {monthName + " " + filter.year}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Div>
            </Grid>
            <Grid item md={4} xs={12}>
              <Div sx={{ mt: 2, mb: 0, mx: 2 }}>
                <TextField
                  select
                  label={t("pages.dsokReports.selectLocation")}
                  value={filter.location || ""}
                  onChange={(e) => updateFilter({ location: e.target.value })}
                  sx={{ width: "100%" }}
                >
                  {locationsMenuItems}
                </TextField>
              </Div>
            </Grid>
            {filter.reportType !== "externalOrders" && (
            <Grid item md={3} xs={12}>
              <Div sx={{ mt: 2, mb: 0, mx: 2 }}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel id="payment-method-label">
                    {t("pages.dsokReports.paymentMethod")}
                  </InputLabel>
                  <Select
                    labelId="payment-method-label"
                    multiple
                    value={filter.paymentMethods}
                    label={t("pages.dsokReports.paymentMethod")}
                    onChange={(e) =>
                      updateFilter({ paymentMethods: e.target.value })
                    }
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t("pages.dsokReports.allPaymentMethods")
                        : selected
                            .map((m) =>
                              t(`pages.dsokReports.paymentMethods.${m}`, m)
                            )
                            .join(", ")
                    }
                  >
                    {ALL_PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        <Checkbox
                          checked={filter.paymentMethods.includes(method)}
                        />
                        <ListItemText
                          primary={t(
                            `pages.dsokReports.paymentMethods.${method}`,
                            method
                          )}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Div>
            </Grid>
            )}
            {filter.month && filter.month === "custom" && (
              <>
                <Div sx={{ mt: 2, mb: 0, mx: 2 }}>
                  <TextField
                    label={t("pages.dsokReports.from")}
                    type="date"
                    value={filter.dateFrom || ""}
                    onChange={(e) => updateFilter({ dateFrom: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Div>
                <Div sx={{ mt: 2, mb: 0, mx: 2 }}>
                  <TextField
                    label={t("pages.dsokReports.to")}
                    type="date"
                    value={filter.dateTo || ""}
                    onChange={(e) => updateFilter({ dateTo: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Div>
              </>
            )}
            {canDisplaySubmitButtons && (
              <Div
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  mb: 0,
                  mx: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    loadData();
                  }}
                >
                  {t("pages.dsokReports.submit")}
                </Button>
              </Div>
            )}
          </Grid>
        </Card>
      </Grid>
      {loading && <CircularProgress sx={{ m: "5px 50%" }} />}
      {!closureData &&
        !salesStatisticsData &&
        !clientsData &&
        !externalOrdersData &&
        !productSalesData && (
          <Div sx={{ textAlign: "center", mt: 2 }}>{noDataPlaceholder}</Div>
        )}
      {closureData && (
        <ClosureReport data={closureData} location={selectedLocation} />
      )}
      {salesStatisticsData && (
        <SalesStatisticsReport
          data={salesStatisticsData}
          location={selectedLocation}
        />
      )}
      {clientsData && (
        <ClientsReport data={clientsData} location={selectedLocation} />
      )}
      {externalOrdersData && <ExternalOrdersReport data={externalOrdersData} />}
      {productSalesData && <ProductSalesReport data={productSalesData} />}
    </>
  );
};

export default DSOKReportsOverview;
